import { useCallback, useEffect, useRef, useState } from "react";
import { zoomAtPoint } from "../../FloorPlanModal/utils/viewport";
import {
  IDENTITY_VIEW,
  MAX_ZOOM,
  MIN_ZOOM_GEO,
  clampZoom,
  exceedsDragThreshold,
  isIdentityView,
  nextZoom,
} from "../utils/viewTransform";

/**
 * Wheel-to-zoom-at-cursor and drag-to-pan for the live canvas.
 *
 * Owns nothing but `{ zoom, pan }` -- both feed straight into
 * `createViewport`, so the canvas re-renders at the new view and NOTHING in
 * the data hooks (which hold metres) is touched. That is what makes zoom/pan
 * survive the 2s personnel/equipment polls: a poll re-renders markers at new
 * metre positions through the SAME viewport, it cannot move the view.
 *
 * @param {*} resetKey  changing it snaps back to fit-to-floor. Hosts pass
 *   machineID: the public dashboard switches machine via history.push, which
 *   keeps this component MOUNTED (same route, new param), so without an
 *   explicit reset you would inherit the previous site's zoom on a different
 *   floor plan. Anything that changes on a poll must never be passed here.
 * @param {boolean} enabled  false locks the view to identity (see LiveCanvas:
 *   line/tunnel layouts, and any layout whose scale is not ready yet).
 * @param {Function} onTransformChange  called with true/false as the view
 *   leaves/returns to fit-to-floor, so the page can pause 自動切換.
 * One rule with or without the map backdrop (D-29): zoom from MIN_ZOOM_GEO
 * to MAX_ZOOM, and the pan is never clamped.
 * @param {boolean} gesturesEnabled  false stops every input path (wheel, drag
 *   pan, zoom buttons) but KEEPS the current view, where `enabled:false`
 *   masks it to identity. The plan editor's 編輯模式 holds the view still
 *   with it, so its own drags and clicks land where the operator zoomed to.
 */
export const useViewTransform = ({
  dimensions,
  canvasSize,
  layoutType,
  enabled,
  resetKey,
  onTransformChange,
  gesturesEnabled = true,
}) => {
  const containerRef = useRef(null);
  const inputEnabled = enabled && gesturesEnabled;
  const [view, setView] = useState(IDENTITY_VIEW);

  const effectiveView = enabled ? view : IDENTITY_VIEW;

  // The event handlers below live on window / a non-passive wheel listener,
  // so they must not close over stale props. Everything they read goes
  // through this ref, which lets their effects depend only on `inputEnabled`.
  const latestRef = useRef(null);
  useEffect(() => {
    latestRef.current = {
      view: effectiveView,
      dimensions,
      canvasSize,
      layoutType,
    };
  });

  const reset = useCallback(() => setView(IDENTITY_VIEW), []);

  // Jump straight to a computed view (the 場外 status's fit). Ignored while
  // input is off, like every other way of moving the view.
  const showView = useCallback(
    (next) => {
      if (!inputEnabled) return;
      setView({ zoom: clampZoom(next.zoom), pan: next.pan });
    },
    [inputEnabled],
  );

  // IDENTITY_VIEW is a module constant, so on mount this sets the same object
  // reference back and React bails out without a re-render.
  useEffect(() => {
    setView(IDENTITY_VIEW);
  }, [resetKey]);

  const isTransformed = !isIdentityView(effectiveView);
  useEffect(() => {
    if (onTransformChange) onTransformChange(isTransformed);
  }, [isTransformed, onTransformChange]);

  // --- buttons: zoom about the canvas centre ------------------------------
  // A button press has no cursor to anchor on, so it holds the CENTRE of the
  // canvas fixed instead. Everything else -- the per-notch factor, the zoom
  // clamp -- is the wheel's, so the two paths cannot drift
  // apart: `steps` is fed through `nextZoom` as a synthetic wheel delta.
  const zoomByStep = useCallback(
    (steps) => {
      if (!inputEnabled) return;
      const el = containerRef.current;
      const { view: current, ...geometry } = latestRef.current;
      // nextZoom reads only the SIGN of the delta, one notch at a time.
      let zoom = current.zoom;
      for (let i = 0; i < Math.abs(steps); i += 1) {
        zoom = nextZoom(zoom, steps > 0 ? -1 : 1);
      }
      if (zoom === current.zoom) return;

      const width = el ? el.clientWidth : geometry.canvasSize.width;
      const height = el ? el.clientHeight : geometry.canvasSize.height;
      const pan = zoomAtPoint({
        point: { x: width / 2, y: height / 2 },
        ...geometry,
        zoom: current.zoom,
        pan: current.pan,
        newZoom: zoom,
      });
      setView({ zoom, pan });
    },
    [inputEnabled],
  );

  // --- wheel: zoom at the cursor -----------------------------------------
  // Attached by hand rather than via onWheel because React registers wheel
  // listeners as PASSIVE at the root, where preventDefault is a no-op and the
  // page would scroll behind the zoom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !inputEnabled) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      const { view: current, ...geometry } = latestRef.current;
      const newZoom = nextZoom(current.zoom, event.deltaY);
      if (newZoom === current.zoom) return;

      const rect = el.getBoundingClientRect();
      // clientLeft/Top is the container's 1px border: children are positioned
      // against the PADDING box, so the border has to come off the cursor
      // position or every zoom drifts by a pixel.
      const point = {
        x: event.clientX - rect.left - el.clientLeft,
        y: event.clientY - rect.top - el.clientTop,
      };

      const pan = zoomAtPoint({
        point,
        ...geometry,
        zoom: current.zoom,
        pan: current.pan,
        newZoom,
      });
      setView({ zoom: newZoom, pan });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [inputEnabled]);

  // --- drag to pan --------------------------------------------------------
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  // Set once a press has travelled past the threshold; read by the
  // capture-phase click handler to swallow the click that mouseup would
  // otherwise deliver to whatever sits under the cursor. NOT cleared on
  // mouseup -- the click fires after it.
  const draggedRef = useRef(false);

  const handleMouseDown = useCallback(
    (event) => {
      if (!inputEnabled || event.button !== 0) return;
      draggedRef.current = false;
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      setDragging(true);
    },
    [inputEnabled],
  );

  useEffect(() => {
    if (!dragging) return undefined;

    const handleMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Below the threshold this is still a click: consume nothing, move
      // nothing. `last` deliberately stays at the press point so the first
      // real pan step includes the whole travel and the floor does not lurch.
      if (
        !draggedRef.current &&
        !exceedsDragThreshold(
          event.clientX - drag.startX,
          event.clientY - drag.startY,
        )
      ) {
        return;
      }
      draggedRef.current = true;

      const dx = event.clientX - drag.lastX;
      const dy = event.clientY - drag.lastY;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;

      const { view: current } = latestRef.current;
      setView({
        zoom: current.zoom,
        pan: { x: current.pan.x + dx, y: current.pan.y + dy },
      });
    };

    const handleUp = () => {
      dragRef.current = null;
      setDragging(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  // Capture phase, on the canvas container: a click that ended a pan is
  // stopped before it reaches the canvas (which clears the personnel
  // selection), a station or a machine (which toggle popovers). A click that
  // never crossed the threshold is left completely alone.
  const handleClickCapture = useCallback((event) => {
    if (!draggedRef.current) return;
    draggedRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  }, []);

  return {
    containerRef,
    zoom: effectiveView.zoom,
    pan: effectiveView.pan,
    isTransformed,
    isDragging: dragging,
    atMinZoom: effectiveView.zoom <= MIN_ZOOM_GEO,
    atMaxZoom: effectiveView.zoom >= MAX_ZOOM,
    reset,
    showView,
    zoomByStep,
    handleMouseDown,
    handleClickCapture,
  };
};

export default useViewTransform;
